import { roundMoney } from "./formatPrice";
import { extractPaymentStatus } from "./paymentStatus";
import { extractTransactionType } from "./transactionType";

const BREAKDOWN_LABELS = {
  paidByCredit: "שולם באשראי",
  paidByCoupon: "שולם בקופון",
  additionalCredit: "זיכוי",
  additionalCharge: "חיוב נוסף",
};

/** Statuses that mean the transaction completed and should count in breakdown. */
const isCountedInBreakdown = (tx) => {
  const key = String(extractPaymentStatus(tx) ?? "")
    .trim()
    .toUpperCase()
    .replace(/^[^-]+-/, "");
  if (!key) return false;
  return (
    key.includes("SUCCESS") ||
    key.includes("PAID") ||
    key.includes("COMPLETE") ||
    key.includes("APPROVED") ||
    key.includes("REFUNDED")
  );
};

const txAmount = (tx) => {
  const n = Number(tx?.sum ?? tx?.amount);
  return Number.isFinite(n) ? n : 0;
};

const sumSuccessfulByType = (transactions, type) =>
  roundMoney(
    (Array.isArray(transactions) ? transactions : [])
      .filter(
        (tx) => extractTransactionType(tx) === type && isCountedInBreakdown(tx),
      )
      .reduce((acc, tx) => acc + txAmount(tx), 0),
  );

/**
 * Builds payment breakdown rows for an admin order.
 * Only includes rows that apply to how this order was paid/adjusted.
 *
 * Sources (OMS + BPS):
 * - paidByCredit: successful PAYMENT txs, else checkout card amount (totalSum − coupon)
 *   when no PAYMENT txs exist yet
 * - paidByCoupon: order.couponDiscount
 * - additionalCredit: successful REFUND txs (e.g. short-pick)
 * - additionalCharge: successful CHARGE txs (e.g. weighed overage)
 */
export const getOrderPaymentBreakdown = (order) => {
  if (!order || typeof order !== "object") return [];

  const totalSum = Number(order.totalSum) || 0;
  const couponDiscount = Number(order.couponDiscount) || 0;
  const transactions = Array.isArray(order.transactions)
    ? order.transactions
    : [];
  const paymentTxs = transactions.filter(
    (tx) => extractTransactionType(tx) === "PAYMENT",
  );
  const paidFromPayments = sumSuccessfulByType(transactions, "PAYMENT");
  const expectedCard = roundMoney(Math.max(0, totalSum - couponDiscount));
  const paidByCredit = paymentTxs.length > 0 ? paidFromPayments : expectedCard;

  const rows = [];

  if (paidByCredit > 0) {
    rows.push({
      key: "paidByCredit",
      label: BREAKDOWN_LABELS.paidByCredit,
      amount: paidByCredit,
    });
  }

  if (couponDiscount > 0) {
    rows.push({
      key: "paidByCoupon",
      label: BREAKDOWN_LABELS.paidByCoupon,
      amount: roundMoney(couponDiscount),
    });
  }

  const additionalCredit = sumSuccessfulByType(transactions, "REFUND");
  if (additionalCredit > 0) {
    rows.push({
      key: "additionalCredit",
      label: BREAKDOWN_LABELS.additionalCredit,
      amount: additionalCredit,
    });
  }

  const additionalCharge = sumSuccessfulByType(transactions, "CHARGE");
  if (additionalCharge > 0) {
    rows.push({
      key: "additionalCharge",
      label: BREAKDOWN_LABELS.additionalCharge,
      amount: additionalCharge,
    });
  }

  return rows;
};

export const getOrderAmountBeforeCoupon = (order) =>
  roundMoney(Number(order?.totalSum) || 0);
